import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import {
  canRefundPayment,
  getPaymentMethodTypeDisplay,
  getPaymentStatusDisplay,
  isPOSPayment,
  Payment,
  PaymentLineItem,
} from '../../../../../../models/payment';
import { capitalizeFirstLetter, dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../../utils';
import { PaymentService } from '../../../../../../services/payment/payment.service';
import { LineItemPurchaseType, PaymentMethodType, PaymentStatus, PaymentType } from '../../../../../../models/payment-enums';
import { TaxDisplayFormat } from '../../../../../../models/organization-config-enums';
import { BOOKING_DETAILS_QUERY_PARAM } from '../../../events/modals/details-modal-query-params';


/** Query parameter that deep-links this modal on a report page. */
export const PAYMENT_DETAILS_QUERY_PARAM = 'paymentDetailsId';


interface RevenueAccountWithPercentage {
  revenueAccount: string;
  percentage: number;
}


@Component({
  selector: 'app-payment-details-modal',
  templateUrl: './payment-details-modal.component.html',
  styleUrls: ['./payment-details-modal.component.css']
})
export class PaymentDetailsModalComponent implements OnInit {
  @Input() paymentId: number;
  @Input() payment: Payment = null;
  // The host runs its own list action so messages, reloads and permissions
  // behave exactly as from the Manage dropdown.
  @Input() onDownloadReceipt: (payment: Payment) => void = null;
  @Input() onViewRefundOptions: (payment: Payment) => void = null;

  public loadingPayment = false;
  public serverError: number = null;

  readonly getPaymentStatusDisplay = getPaymentStatusDisplay;
  readonly getPaymentMethodTypeDisplay = getPaymentMethodTypeDisplay;
  readonly capitalizeFirstLetter = capitalizeFirstLetter;
  readonly dateStringFromISO8601 = dateStringFromISO8601;
  readonly timeStringFromISO8601 = timeStringFromISO8601;
  readonly PaymentStatus = PaymentStatus;
  readonly PaymentType = PaymentType;
  readonly PaymentMethodType = PaymentMethodType;
  readonly LineItemPurchaseType = LineItemPurchaseType;
  public readonly isPOSPayment = isPOSPayment;
  public readonly canRefundPayment = canRefundPayment;
  public readonly TaxDisplayFormat = TaxDisplayFormat;

  constructor(
    public activeModal: NgbActiveModal,
    public orgConfig: OrganizationConfigService,
    private paymentService: PaymentService
  ) {}

  ngOnInit() {
    if (this.payment === null) {
      this.loadPayment();
    }
  }

  public showDownloadReceipt(): boolean {
    return !!this.onDownloadReceipt && !!this.payment && !isPOSPayment(this.payment);
  }

  public showRefundOptions(): boolean {
    return !!this.onViewRefundOptions && !!this.payment && canRefundPayment(this.payment);
  }

  public handleDownloadReceipt() {
    const payment = this.payment;
    this.activeModal.close();
    this.onDownloadReceipt(payment);
  }

  public handleViewRefundOptions() {
    const payment = this.payment;
    this.activeModal.close();
    this.onViewRefundOptions(payment);
  }

  public getBookingId(li: PaymentLineItem): number | null {
    if (li.purchase_type !== LineItemPurchaseType.booking || typeof li.booking_id !== 'number') {
      return null;
    }
    return li.booking_id;
  }

  public getBookingQueryParams(li: PaymentLineItem): { [key: string]: number } {
    return { [BOOKING_DETAILS_QUERY_PARAM]: this.getBookingId(li) };
  }

  public getRevenueAccountsWithPercentages(): RevenueAccountWithPercentage[] {
    const rawps: RevenueAccountWithPercentage[] = [];
    let unmapped = 100;

    for (const revenueAccount of Object.keys(this.payment.revenue_account_codes)) {
      const percentage = Math.round(
        this.payment.revenue_account_codes[revenueAccount] * 100
      );
      unmapped -= percentage;
      rawps.push({
        revenueAccount,
        percentage
      });
    }

    if (unmapped > 0) {
      rawps.push({
        revenueAccount: 'Do Not Report',
        percentage: unmapped
      });
    }

    return rawps;
  }

  private loadPayment() {
    this.loadingPayment = true;

    this.paymentService
      .getPayment(this.paymentId)
      .then(payment => { this.payment = payment; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingPayment = false; });
  }

}
